import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {NodeDetailsSidepanelContentProps} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {TaskSchemaNode} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {useAllSchemasById} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useAllSchemasById';
import {
  ProcessTaskSchema,
  ProcessTaskSchemaEditorContent,
  ProcessTaskSchemaEditorContentSections,
} from 'processes/settings/ProcessTaskSchemaEditor';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import InputOutputFieldTables from './InputOutputFieldTables';
import {NodeDetailsSidepanelContext} from './NodeDetailsSidepanelContext';
import TransitionFieldInfoSection from './TransitionFieldInfoSection';

export enum NodeSettingsTabKeys {
  FLOWCHART_SETTINGS = 'FLOWCHART_SETTINGS',
  NODE_FIELD_DETAILS = 'NODE_FIELD_DETAILS',
  TASK_SCHEMA_DETAILS = 'TASK_SCHEMA_DETAILS',
}

export function TaskSchemaDetailsTab(props: NodeDetailsSidepanelContentProps): JSX.Element {
  const context = NodeDetailsSidepanelContext.useContext();
  // TODO(BNCH-126759): Update types to better handle when selected node is task vs logic node
  const selectedNode = context.selectedNode as TaskSchemaNode;
  const {
    processTaskSchemas: allTaskSchemas,
    storableSchemas,
    tagSchemas,
    resultSchemas,
    statusLifecycles,
    haveSchemasLoaded,
  } = useAllSchemasById(context.flowchartTaskSchema);

  const taskSchemaId = selectedNode.data.taskSchemaId;
  const taskSchema = context.allSchemasById[taskSchemaId];

  return !haveSchemasLoaded ? (
    <CenteredHorizontalThrobber />
  ) : (
    <div className={css(styles.taskSchemaEditorContainer)}>
      <ProcessTaskSchemaEditorContent
        processTaskSchema={taskSchema as ProcessTaskSchema}
        storableSchemas={storableSchemas}
        tagSchemas={tagSchemas}
        taskSchemas={allTaskSchemas}
        statusLifecycles={statusLifecycles}
        resultSchemas={resultSchemas}
        isAnyPartOfFlowchartConfigInaccessible={null}
        sectionsToHide={[
          ProcessTaskSchemaEditorContentSections.TASK_FIELDS,
          ProcessTaskSchemaEditorContentSections.OUTPUT,
          ProcessTaskSchemaEditorContentSections.FLOWCHART,
        ]}
        refetchProcessSchema={context.refetchFlowchartConfig}
        locked={props.locked}
      />
    </div>
  );
}

export function NodeFieldDetailsTab(
  props: NodeDetailsSidepanelContentProps & {selectedNode: TaskSchemaNode}
): JSX.Element {
  const context = NodeDetailsSidepanelContext.useContext();

  return (
    <div className={css(styles.tabContentContainer)}>
      {props.selectedFieldId != null ? (
        <TransitionFieldInfoSection
          selectedFieldId={props.selectedFieldId}
          node={context.selectedNode as TaskSchemaNode}
          flowchart={context.interactiveFlowchart}
          allSchemasById={context.allSchemasById}
          flowchartTaskSchema={context.flowchartTaskSchema}
          customMappingDataByEdgeId={context.customMappingDataByEdgeId}
          onChangeCustomMappingData={context.onChangeCustomMappingData}
          onChangeSelectedFieldId={props.onChangeSelectedFieldId}
          transitionSourceByNodeIdPair={context.transitionSourceByNodeIdPair}
          locked={props.locked}
        />
      ) : (
        <InputOutputFieldTables
          selectedNode={context.selectedNode}
          interactiveFlowchart={context.interactiveFlowchart}
          allSchemasById={context.allSchemasById}
          flowchartTaskSchema={context.flowchartTaskSchema}
          onChangeSelectedFieldId={props.onChangeSelectedFieldId}
          locked={props.locked}
          refetchFlowchartConfig={context.refetchFlowchartConfig}
        />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  tabContentContainer: {
    padding: '36px',
    marginTop: '-23px',
  },
  noFieldSelectedText: {
    fontSize: '16px',
    color: Color.GRAY_900,
  },
  taskSchemaEditorContainer: {
    padding: '0px 16px',
  },
});
